import React from "react";
import cx from "classnames";
import _compact from "lodash/compact";
import checkIsPerformer from "shared-auth/src/utils/checkIsPerformer";
import { commaJoin } from "shared-utils";
import { useTranslation } from "i18n";
import { dataTestIds } from "data-testids";
import { InfoThinIcon } from "assets";
import { Tooltip } from "component-library";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import { useGetBasicInfo } from "pages/Profile/hooks/useGetBasicInfo";
import ExpandableText from "pages/Profile/components/common/ExpandableText";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { BASIC_DETAILS_FULL_PATH } from "app/config/routes";

const TEXT_EXPANDABLE_LIMIT = 250;
const TEXT_SHOW_MORE_LIMIT = 50;

const sectionTextBaseClass = "break-words dark:text-white/70 flex flex-1 gap-1";

interface Props {
  isEdit?: boolean;
}

const BasicInfoSection = ({ isEdit }: Props) => {
  const { isPublicView } = usePublicView();
  const { t } = useTranslation();
  const { basicInfo, basicInfoConfiguration, isLoading } = useGetBasicInfo();
  const sensitiveFieldsTooltipText = t(
    "performer:profile.basicInfo.sensitiveFields.tooltip"
  );

  const isPerformerPrivateView = checkIsPerformer() && !isPublicView;

  const specs = basicInfo
    ? [
        {
          label: t("common:label.location"),
          value: commaJoin(basicInfo.locations, ",\n"),
          classNames: "whitespace-pre-wrap",
        },
        {
          label: t("common:label.workPermits"),
          value: commaJoin(basicInfo.visasAndPermits),
          tooltipText:
            !basicInfoConfiguration.isVisibleVisasAndPermits &&
            isPerformerPrivateView
              ? sensitiveFieldsTooltipText
              : undefined,
        },
        {
          label: t("common:label.gender"),
          value: commaJoin(basicInfo.genders),
          tooltipText:
            !basicInfoConfiguration.isVisibleGender && isPerformerPrivateView
              ? sensitiveFieldsTooltipText
              : undefined,
        },
        {
          label: t("common:label.disability"),
          value: commaJoin(basicInfo.disabilities),
          tooltipText:
            !basicInfoConfiguration.isVisibleDisability &&
            isPerformerPrivateView
              ? sensitiveFieldsTooltipText
              : undefined,
        },
        { label: t("common:label.actualAge"), value: basicInfo.actualAge },
        { label: t("common:label.playingAge"), value: basicInfo.playingAge },
        { label: t("common:label.height"), value: basicInfo.height },
        {
          label: t("common:label.nationalities"),
          value: commaJoin(basicInfo.nationalities),
        },
        {
          label: t("common:label.ethnicityOrHeritage"),
          value: commaJoin(basicInfo.ethnicity),
          tooltipText:
            !basicInfoConfiguration.isVisibleHeritageAndEthnicity &&
            isPerformerPrivateView
              ? sensitiveFieldsTooltipText
              : undefined,
        },
        {
          label: t("common:label.membership"),
          value: commaJoin(basicInfo.memberships),
        },
        {
          label: t("common:label.appearance"),
          value: commaJoin(basicInfo.appearances),
        },
        { label: t("common:label.complexionShades"), value: basicInfo.makeUp },
        { label: t("common:label.twin"), value: basicInfo.hasTwin },
        { label: t("common:label.hairColour"), value: basicInfo.hairColor },
        { label: t("common:label.hairLength"), value: basicInfo.hairLength },
        { label: t("common:label.facialHair"), value: basicInfo.facialHair },
        { label: t("common:label.eyeColour"), value: basicInfo.eyeColor },
        {
          label: t("common:label.voiceStyles"),
          value: commaJoin(basicInfo.voiceStyle),
        },
        { label: t("common:label.vocalRange"), value: basicInfo.vocalRange },
        { label: t("common:label.vocalAge"), value: basicInfo.vocalAge },
        { label: t("common:label.hat"), value: basicInfo.hatSize },
        { label: t("common:label.chest"), value: basicInfo.chestSize },
        { label: t("common:label.waist"), value: basicInfo.waistSize },
        { label: t("common:label.hips"), value: basicInfo.hipsSize },
        { label: t("common:label.insideArm"), value: basicInfo.insideArmSize },
        { label: t("common:label.insideLeg"), value: basicInfo.insideLegSize },
        { label: t("common:label.weight"), value: basicInfo.weight },
        { label: t("common:label.collar"), value: basicInfo.collarSize },
        { label: t("common:label.shoeSize"), value: basicInfo.shoeSize },
        { label: t("common:label.ukDressSize"), value: basicInfo.dressSize },
      ]
    : [];

  return (
    <ProfileCard
      className="grid grid-cols-1 gap-1.5 text-s"
      isEdit={isEdit}
      isLoading={isLoading}
      editToPath={BASIC_DETAILS_FULL_PATH}
      data-testid={dataTestIds.profile["Profile.ProfileCard.basicInfoSection"]}
    >
      <h2 className="hidden">{t("performer:profile.section.details")}</h2>
      {_compact(
        specs.map((item) => {
          if (!item.value) return null;

          const tooltip = item.tooltipText && (
            <Tooltip
              className="h-content mt-auto flex"
              content={item.tooltipText}
            >
              <InfoThinIcon className="mt-auto size-[20px] cursor-pointer opacity-70" />
            </Tooltip>
          );

          return (
            <div
              key={item.label}
              className="flex flex-wrap gap-1.5"
              data-testid={`${dataTestIds.profile["Profile.BaicInfoSection.itemDetail"]}-${item.label}`}
            >
              <span className="shrink-0 font-semibold dark:text-white/90">
                {item.label}:
              </span>
              {item.value.length >= TEXT_EXPANDABLE_LIMIT ? (
                <ExpandableText
                  text={item.value}
                  limit={TEXT_SHOW_MORE_LIMIT}
                  textClassName={cx(
                    "text-s",
                    sectionTextBaseClass,
                    item.classNames
                  )}
                  tooltip={tooltip}
                />
              ) : (
                <span className={cx(sectionTextBaseClass, item.classNames)}>
                  {item.value}
                  {tooltip}
                </span>
              )}
            </div>
          );
        })
      )}
    </ProfileCard>
  );
};

export default BasicInfoSection;
